// src/services/profile/profile.remote.js
// User profile CRUD backed by public.users plus selbstbestimmte Datenfreigabe
// (siehe Migration 20260655000354_user_profile_fields_and_visibility.sql).

import { getSupabaseClient, getSupabaseUser } from "@tennis-matches/services/supabase";
import { loadUserSettings, saveUserSettings } from "../settings/index.js";
import { DEFAULT_VISIBILITY, normalizeVisibilityMap, visibilityMapToRemote } from "./profile-visibility.js";

function __asStr(x) {
  return String(x ?? "").trim();
}

function __isRpcMissing(error) {
  const code = String(error?.code || "");
  const msg = String(error?.message || "").toLowerCase();
  return (
    code === "42883" ||
    code === "PGRST202" ||
    code === "PGRST204" ||
    msg.includes("could not find the function") ||
    msg.includes("update_my_profile_fields") ||
    msg.includes("update_my_profile_visibility")
  );
}

function __nullableStr(x) {
  const s = __asStr(x);
  return s.length > 0 ? s : null;
}

function __nullableDate(x) {
  const s = __asStr(x);
  return s.length > 0 ? s : null;
}

function __isLegacyProfileSchemaError(error) {
  const code = String(error?.code || "");
  const msg = String(error?.message || "").toLowerCase();
  const status = Number(error?.status ?? error?.statusCode);
  return (
    code === "42703" ||
    code === "PGRST204" ||
    status === 400 ||
    (msg.includes("column") && msg.includes("does not exist")) ||
    msg.includes("schema cache")
  );
}

async function __loadMyProfileMinimal(supabase, userId) {
  const { data: minData, error: minErr } = await supabase
    .from("users")
    .select("id,email,display_name,avatar_url,primary_tenant_id,created_at,updated_at")
    .eq("id", userId)
    .maybeSingle();
  if (minErr) return { ok: false, reason: "db-error", error: minErr, profile: null };
  if (!minData) return { ok: false, reason: "not-found", profile: null };
  return {
    ok: true,
    profile: { ...__profileFromRow(minData) },
    visibility: { ...DEFAULT_VISIBILITY },
    reason: "legacy-schema",
  };
}

function __profileFromRow(data) {
  return {
    id: __asStr(data?.id),
    email: __asStr(data?.email),
    displayName: __asStr(data?.display_name),
    avatarUrl: __asStr(data?.avatar_url),
    primaryTenantId: __asStr(data?.primary_tenant_id),
    firstName: __asStr(data?.first_name),
    lastName: __asStr(data?.last_name),
    birthDate: __asStr(data?.birth_date),
    city: __asStr(data?.city),
    phone: __asStr(data?.phone),
    createdAt: __asStr(data?.created_at),
    updatedAt: __asStr(data?.updated_at),
  };
}

async function __readVisibilityFromSettings(supabase, userId) {
  // public.user_settings.settings.profile_visibility ist die Quelle.
  // Wenn (noch) leer → Default-Map.
  try {
    const { data, error } = await supabase
      .from("user_settings")
      .select("settings")
      .eq("user_id", userId)
      .maybeSingle();
    if (error) {
      const code = String(error?.code || "");
      if (code !== "42P01" && code !== "42703") {
        // Tabelle/Spalte fehlt → Default. Andere Fehler → Default + Log.
        console.warn("[profile] visibility load failed, falling back:", error);
      }
      return { ...DEFAULT_VISIBILITY };
    }
    const raw = data?.settings?.profile_visibility;
    return normalizeVisibilityMap(raw);
  } catch (e) {
    console.warn("[profile] visibility load error:", e);
    return { ...DEFAULT_VISIBILITY };
  }
}

export async function loadMyProfile() {
  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase", profile: null };

  const u = await getSupabaseUser();
  const userId = __asStr(u?.id);
  if (!userId) return { ok: false, reason: "not-authenticated", profile: null };

  const { data, error } = await supabase
    .from("users")
    .select(
      "id,email,display_name,avatar_url,primary_tenant_id,first_name,last_name,birth_date,city,phone,created_at,updated_at"
    )
    .eq("id", userId)
    .maybeSingle();

  if (error) {
    if (__isLegacyProfileSchemaError(error)) {
      return __loadMyProfileMinimal(supabase, userId);
    }
    return { ok: false, reason: "db-error", error, profile: null };
  }
  if (!data) return { ok: false, reason: "not-found", profile: null };

  const visibility = await __readVisibilityFromSettings(supabase, userId);

  return {
    ok: true,
    profile: __profileFromRow(data),
    visibility,
  };
}

/**
 * Legacy: setzt display_name + avatar_url (klassischer Profil-Pfad).
 * Bleibt für Aufrufer wie Onboarding/AppShell erhalten.
 */
export async function updateMyProfile({ displayName, avatarUrl } = {}) {
  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };

  const u = await getSupabaseUser();
  const userId = __asStr(u?.id);
  if (!userId) return { ok: false, reason: "not-authenticated" };

  const patch = { updated_at: new Date().toISOString() };
  if (displayName !== undefined) patch.display_name = __asStr(displayName);
  if (avatarUrl !== undefined) patch.avatar_url = __asStr(avatarUrl);
  if (Object.keys(patch).length <= 1) return { ok: false, reason: "nothing-to-update" };

  const { error } = await supabase.from("users").update(patch).eq("id", userId);
  if (error) return { ok: false, reason: "db-error", error };

  if (avatarUrl !== undefined && typeof globalThis !== "undefined" && globalThis.window?.dispatchEvent) {
    globalThis.window.dispatchEvent(
      new CustomEvent("tm:my-profile-updated", {
        detail: { avatarUrl: __asStr(avatarUrl) },
      })
    );
  }

  return { ok: true };
}

const USER_AVATAR_BUCKET = "user-avatars";
const USER_AVATAR_MAX_BYTES = 5 * 1024 * 1024;
const USER_AVATAR_MIME_EXT = Object.freeze({
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/gif": "gif",
});

const USER_AVATAR_EXT_MIME = Object.freeze({
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  gif: "image/gif",
});

function __resolveAvatarMimeAndExt(file) {
  const mime = __asStr(file?.type).toLowerCase();
  if (mime && USER_AVATAR_MIME_EXT[mime]) {
    return { mime, ext: USER_AVATAR_MIME_EXT[mime] };
  }
  const name = __asStr(file?.name).toLowerCase();
  const ext = name.includes(".") ? name.split(".").pop() : "";
  const inferredMime = USER_AVATAR_EXT_MIME[ext];
  if (inferredMime) {
    return { mime: inferredMime, ext: USER_AVATAR_MIME_EXT[inferredMime] };
  }
  return { mime: "", ext: "" };
}

/**
 * Lädt ein Profilbild in Supabase Storage und setzt avatar_url in public.users.
 *
 * @param {File|Blob} file
 * @returns {Promise<{ ok: boolean, avatarUrl?: string, reason?: string, error?: unknown }>}
 */
export async function uploadMyProfileAvatarRemote(file) {
  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };

  const u = await getSupabaseUser();
  const userId = __asStr(u?.id);
  if (!userId) return { ok: false, reason: "not-authenticated" };

  const blob = file && typeof file === "object" ? file : null;
  if (!blob) return { ok: false, reason: "invalid-file" };

  const { mime, ext } = __resolveAvatarMimeAndExt(blob);
  if (!mime || !ext) return { ok: false, reason: "invalid-type" };

  const size = Number(blob.size);
  if (!Number.isFinite(size) || size <= 0) return { ok: false, reason: "invalid-file" };
  if (size > USER_AVATAR_MAX_BYTES) return { ok: false, reason: "too-large" };

  const path = `${userId}/avatar.${ext}`;
  const { error: uploadError } = await supabase.storage.from(USER_AVATAR_BUCKET).upload(path, blob, {
    upsert: true,
    contentType: mime,
    cacheControl: "3600",
  });
  if (uploadError) return { ok: false, reason: "upload-failed", error: uploadError };

  const { data: pub } = supabase.storage.from(USER_AVATAR_BUCKET).getPublicUrl(path);
  const avatarUrl = __asStr(pub?.publicUrl);
  if (!avatarUrl) return { ok: false, reason: "no-public-url" };

  const cacheBustedUrl = `${avatarUrl}${avatarUrl.includes("?") ? "&" : "?"}v=${Date.now()}`;
  const res = await updateMyProfile({ avatarUrl: cacheBustedUrl });
  if (!res?.ok) return res;
  return { ok: true, avatarUrl: cacheBustedUrl };
}

async function __updateMyProfileFieldsFallback(userId, fields) {
  const supabase = await getSupabaseClient();
  if (!supabase || !userId) return { ok: false, reason: "no-supabase" };

  const displayName = [__asStr(fields.firstName), __asStr(fields.lastName)]
    .filter(Boolean)
    .join(" ");
  const patch = {
    first_name: __nullableStr(fields.firstName),
    last_name: __nullableStr(fields.lastName),
    birth_date: __nullableDate(fields.birthDate),
    city: __nullableStr(fields.city),
    phone: __nullableStr(fields.phone),
    updated_at: new Date().toISOString(),
  };
  if (displayName) patch.display_name = displayName;

  const { error } = await supabase.from("users").update(patch).eq("id", userId);
  if (error) {
    const code = String(error?.code || "");
    if (code === "42703" && displayName) {
      return updateMyProfile({ displayName });
    }
    if (code === "42703") return { ok: true, reason: "legacy-schema" };
    return { ok: false, reason: "db-error", error };
  }
  return { ok: true, displayName };
}

async function __updateMyProfileVisibilityFallback(visibilityMap) {
  const current = await loadUserSettings();
  const existing = current?.settings && typeof current.settings === "object" ? current.settings : {};
  const normalized = normalizeVisibilityMap(visibilityMap);
  const remote = visibilityMapToRemote(normalized);
  const next = {
    ...existing,
    profile_visibility: remote,
    profileVisibility: normalized,
  };
  const res = await saveUserSettings(next);
  if (!res?.ok) return { ok: false, reason: res?.reason || "save-failed" };
  return { ok: true, visibility: normalized, persisted: res.persisted !== false };
}

/**
 * Setzt den vollständigen Stand der 5 selbstbestimmten Profilfelder.
 * Aufrufer MUSS immer alle Felder schicken (NULL/leer = Feld leeren).
 * display_name wird in der RPC aus first_name + last_name abgeleitet.
 */
export async function updateMyProfileFields({
  firstName,
  lastName,
  birthDate,
  city,
  phone,
} = {}) {
  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };

  const u = await getSupabaseUser();
  const userId = __asStr(u?.id);
  if (!userId) return { ok: false, reason: "not-authenticated" };

  const fields = { firstName, lastName, birthDate, city, phone };

  const { data, error } = await supabase.rpc("update_my_profile_fields", {
    p_first_name: __nullableStr(firstName),
    p_last_name: __nullableStr(lastName),
    p_birth_date: __nullableDate(birthDate),
    p_city: __nullableStr(city),
    p_phone: __nullableStr(phone),
  });

  if (error) {
    if (__isRpcMissing(error)) {
      return __updateMyProfileFieldsFallback(userId, fields);
    }
    return { ok: false, reason: "db-error", error };
  }
  return {
    ok: Boolean(data?.ok ?? true),
    displayName: __asStr(data?.display_name),
  };
}

/**
 * Setzt die Sichtbarkeits-Map. Eingabe in camelCase wird zu snake_case mapped.
 *
 * @param {{firstName?:string,lastName?:string,birthDate?:string,city?:string,phone?:string}} visibilityMap
 */
export async function updateMyProfileVisibility(visibilityMap) {
  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };

  const payload = visibilityMapToRemote(visibilityMap);

  const { data, error } = await supabase.rpc("update_my_profile_visibility", {
    p_visibility: payload,
  });

  if (error) {
    if (__isRpcMissing(error)) {
      return __updateMyProfileVisibilityFallback(visibilityMap);
    }
    return { ok: false, reason: "db-error", error };
  }
  return {
    ok: Boolean(data?.ok ?? true),
    visibility: normalizeVisibilityMap(data?.visibility),
  };
}

/**
 * Holt das sichtbarkeits-gefilterte Profil eines anderen Users.
 * Liefert Resolver-Output (snake_case) inkl. display_name/avatar_url/visibility.
 */
export async function resolveUserProfileRemote(userId) {
  const id = __asStr(userId);
  if (!id) return { ok: false, reason: "missing-user", profile: null };

  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase", profile: null };

  const { data, error } = await supabase.rpc("resolve_user_profile", { p_user_id: id });
  if (error) return { ok: false, reason: "db-error", error, profile: null };
  if (!data || data?.ok === false) {
    return { ok: false, reason: __asStr(data?.reason) || "not-found", profile: null };
  }
  return { ok: true, profile: data };
}

/**
 * Batch-Variante: liefert eine Map { userId -> profile-jsonb }.
 *
 * @param {string[]} userIds
 */
export async function resolveUsersProfilesRemote(userIds) {
  const ids = Array.isArray(userIds)
    ? Array.from(new Set(userIds.map((x) => __asStr(x)).filter(Boolean)))
    : [];
  if (ids.length === 0) return { ok: true, profiles: {} };

  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase", profiles: {} };

  const { data, error } = await supabase.rpc("resolve_users_profiles", { p_user_ids: ids });
  if (error) return { ok: false, reason: "db-error", error, profiles: {} };

  const profiles = data?.profiles && typeof data.profiles === "object" ? data.profiles : {};
  return { ok: true, profiles };
}
